import {
    SectionQuizScene,
    QuestionQuizScene,
    PostSubmitQuizScene
} from './quiz_scenes';

import * as is from 'utilities/is';
import { randomOrder, orderSpecifically } from 'utilities/list';

export class QuizSceneController {
    constructor({
        descendants = [],
        initialFormData = {},
        initialState = {}
    }) {
        this.lookup = {};
        this.history = initialState.history || [];
        this.initialFormData = initialFormData;
        this.preSummarySceneLength = 0;
        this.ordering = initialState.ordering || determineOrder(descendants);
        this.scenes = this.buildGraph(descendants);
        this.currentSceneIndex = this.getNotSkippedScene(initialState.currentSceneIndex || 0);
    }

    buildGraph(descendants = []) {
        let scene;
        const graph = [];
        const sections = descendants.filter(d => d.type === 'quizsection');
        const messages = descendants.filter(d => d.type === 'pop' && d.intent?.endsWith('Message'));
        this.preSummarySceneLength = 0;
        this.numQuestions = 0;
        this.questions = [];
        
        for (const [i, section] of sections.entries()) {
            const sectionScene = new SectionQuizScene({
                index: i,
                last: i === sections.length - 1,
                components: [section]                
            });
            
            let questions = section.descendants?.filter(d => d.type === 'question') || [];
            questions = this.applyOrdering(questions, section);
            
            graph.push(sectionScene);
            this.lookup[section.id] = sectionScene;
            this.preSummarySceneLength++;

            for (const [j, question] of questions.entries()) {
                const defaultValue = this.initialFormData[question.id];
                let answers = question.descendants?.filter(d => d.type === 'answer') || [];
                answers = this.applyOrdering(answers, question);

                scene = new QuestionQuizScene({                    
                    defaultValue,
                    index: j,
                    last: j === questions.length - 1,
                    parent: sectionScene,
                    components: [{ ...question, answers }]
                });

                graph.push(scene);
                this.numQuestions++;
                this.questions.push(scene);
                this.lookup[question.id] = scene;
                this.preSummarySceneLength++;
            }
        }

        if (messages.length) {
            scene = new PostSubmitQuizScene({
                last: true,
                questions: this.questions,
                components: [...messages]
            });
    
            graph.push(scene);
            messages.forEach(m => this.lookup[m.id] = scene);
            this.hasSummary = true;
        }
        
        return graph;
    }

    get currentScene() {
        return this.applyGlobals(
                this.scenes[this.currentSceneIndex] ? 
                    this.scenes[this.currentSceneIndex].attributes : 
                    {}
        )
    }

    setScene(id = '', bypassHistory = false) {
        const node = this.lookup[id];

        if (node) {
            let sceneIndex = this.scenes.indexOf(node);
            sceneIndex = this.getNotSkippedScene(sceneIndex);
            const scene = this.scenes[sceneIndex]

            if (!scene) {
                return this.currentScene;
            }

            if (!bypassHistory) {
                this.history.push(this.currentSceneIndex);
            }
            
            this.currentSceneIndex = sceneIndex;
            return this.applyGlobals(scene.attributes);
        }

        return this.currentScene;
    }

    prevScene() {
        const sceneIndex = this.history.pop();
        
        if (is.defined(sceneIndex)) {
            const scene = this.scenes[sceneIndex];
            this.currentSceneIndex = sceneIndex;

            return this.applyGlobals(scene.attributes);
        }

        return this.currentScene;
    }

    nextScene() {
        const { scenes, currentSceneIndex } = this;
        let nextSceneIndex = this.getNotSkippedScene(currentSceneIndex + 1);

        let scene = scenes[nextSceneIndex];

        if (!scene) {
            return this.currentScene;
        }

        if (this.currentSceneIndex !== nextSceneIndex) {
            this.history.push(this.currentSceneIndex);
            this.currentSceneIndex = nextSceneIndex; 
        }
               
        return this.applyGlobals(scene.attributes);
    }

    getNotSkippedScene(sceneIndex) {
        while(this.scenes[sceneIndex] && this.scenes[sceneIndex].shouldSkip) {
            sceneIndex++;
        }

        return sceneIndex;
    }

    lastScene() {
        if (this.currentSceneIndex !== this.lastIndex) {
            this.history.push(this.currentSceneIndex);
            this.currentSceneIndex = this.lastIndex;
        }        
        
        return this.currentScene;
    }

    restart() {
        this.history.length = 0;
        this.currentSceneIndex = 0;
        return this.currentScene;
    }

    applyOrdering(list = [], { id = '', properties = {} }) {
        if (!this.ordering[id]) {
            return list;
        }

        if (
            this.ordering[id].direction === 'ascending' &&
            (properties.shuffleQuestionOrder || properties.shuffleAnswerOrder)
        ) {
            this.ordering[id] = randomizeOrder(list);
        } else if (
            this.ordering[id].direction === 'shuffle' &&
            !properties.shuffleQuestionOrder &&
            !properties.shuffleAnswerOrder
        ) {
            this.ordering[id] = ascendizeOrder(list);
        }

        return (
            list.sort(
                orderSpecifically(list, this.ordering[id].order)
            )
        )
    }
    
    applyGlobals(obj = {}) {
        return {
            ...obj,
            history: this.history,
            ordering: this.ordering,
            hasSummary: this.hasSummary,
            numQuestions: this.numQuestions,
            currentSceneIndex: this.currentSceneIndex
        }
    }

    set descendants(descendants = []) {
        this.scenes = this.buildGraph(descendants);
        this.currentSceneIndex = this.getNotSkippedScene(this.currentSceneIndex);
    }

    get currentProgress() {
        return (
            this.scenes
                .slice(0, this.currentSceneIndex + 1)
                .filter(s => s.contributesProgress)
                .length
        )
    }

    get totalProgress() {
        return this.scenes.filter(s => s.contributesProgress).length;
    }

    get lastIndex() {
        return this.scenes.length - 1;
    }

    get isLastScene() {
        return this.currentSceneIndex === this.lastIndex;
    }

    get canComplete() {
        if (this.hasSummary) {
            return this.isLastScene;
        }

        return true;
    }
}

function randomizeOrder(list = []) {
    return {
        direction: 'shuffle',
        order: list.map((l,i) => i).sort(randomOrder)
    }
}

function ascendizeOrder() {
    return {
        direction: 'ascending',
        order: [] // use list items as ordered at the time of access
    }
}

function determineOrder(descendants = []) {
    return (
        descendants.reduce((ordering, descendant) => {
            if (descendant.type === 'quizsection') {
                const questions = (
                    descendant.descendants?.filter(d => d.type === 'question') || []
                );

                ordering[descendant.id] = 
                    descendant?.properties?.shuffleQuestionOrder ?
                        randomizeOrder(questions) :
                        ascendizeOrder(questions);
                
                questions.forEach(q => {
                    const answers = (
                        q.descendants?.filter(d => d.type === 'answer') || []    
                    );

                    ordering[q.id] = 
                        q.properties?.shuffleAnswerOrder ?
                            randomizeOrder(answers) :
                            ascendizeOrder(answers);
                })
            }

            return ordering;
        }, {})
    )
}